import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const VenueCreateModal = ({ isOpen, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    // 시설 정보
    name: '',
    phone: '',
    address: '',
    parking: '',
    email: '',
    website: '',
    latitude: '',
    longitude: '',
    // 결제 설정
    bankName: '',
    accountNumber: '',
    accountHolder: '',
    paymentDeadline: '3',
    // 운영 설정
    defaultCourts: '',
    defaultDuration: '120',
    minParticipants: '',
    maxParticipants: '',
    availableSports: ['pickleball']
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      name: '',
      phone: '',
      address: '',
      parking: '',
      email: '',
      website: '',
      latitude: '',
      longitude: '',
      bankName: '',
      accountNumber: '',
      accountHolder: '',
      paymentDeadline: '3',
      defaultCourts: '',
      defaultDuration: '120',
      minParticipants: '',
      maxParticipants: '',
      availableSports: ['pickleball']
    });
    setErrors({});
    setIsLoading(false);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onClose]);

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = '시설명을 입력해주세요';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = '연락처를 입력해주세요';
    }

    if (!formData?.address?.trim()) {
      newErrors.address = '주소를 입력해주세요';
    }

    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors(prev => ({ ...prev, submit: '' }));

    try {
      await onCreate(formData);
      onClose();
    } catch (err) {
      setErrors(prev => ({ ...prev, submit: err?.message || '저장 중 오류가 발생했습니다' }));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-300 flex items-start justify-center pt-[8vh] px-4 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="fixed inset-0 bg-background z-250" aria-hidden="true" />
      <div className="relative z-300 w-full max-w-2xl bg-card rounded-xl shadow-2xl border border-border my-8">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                시설 추가
              </h2>
              <p className="text-sm caption text-muted-foreground">
                시설 기본 정보를 입력해주세요
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic active-press disabled:opacity-50"
              aria-label="닫기"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 시설 정보 카드 */}
            <div className="bg-muted/30 border border-border rounded-xl p-4 md:p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Building2" size={18} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 className="text-base font-heading font-semibold text-foreground">시설 정보</h3>
                  <p className="text-xs text-muted-foreground">기본 시설 정보를 입력하세요</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Input
                    label="시설명"
                    type="text"
                    placeholder="시설 이름을 입력하세요"
                    value={formData?.name}
                    onChange={(e) => handleChange('name', e?.target?.value)}
                    error={errors?.name}
                    required
                    disabled={isLoading}
                  />
                  <Input
                    label="연락처"
                    type="tel"
                    placeholder="010-1234-5678"
                    value={formData?.phone}
                    onChange={(e) => handleChange('phone', e?.target?.value)}
                    error={errors?.phone}
                    required
                    disabled={isLoading}
                  />
                </div>

                <Input
                  label="주소"
                  type="text"
                  placeholder="시설 주소를 입력하세요"
                  value={formData?.address}
                  onChange={(e) => handleChange('address', e?.target?.value)}
                  error={errors?.address}
                  required
                  disabled={isLoading}
                />

                <Input
                  label="주차 정보"
                  type="text"
                  placeholder="주차 가능 대수 및 안내사항"
                  value={formData?.parking}
                  onChange={(e) => handleChange('parking', e?.target?.value)}
                  disabled={isLoading}
                />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Input
                    label="이메일"
                    type="email"
                    placeholder="facility@example.com"
                    value={formData?.email}
                    onChange={(e) => handleChange('email', e?.target?.value)}
                    error={errors?.email}
                    disabled={isLoading}
                  />
                  <Input
                    label="웹사이트"
                    type="url"
                    placeholder="https://example.com"
                    value={formData?.website}
                    onChange={(e) => handleChange('website', e?.target?.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            {/* 결제 설정 카드 */}
            <div className="bg-muted/30 border border-border rounded-xl p-4 md:p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="CreditCard" size={18} color="var(--color-success)" />
                </div>
                <div>
                  <h3 className="text-base font-heading font-semibold text-foreground">결제 설정</h3>
                  <p className="text-xs text-muted-foreground">참가비 입금 계좌 정보를 설정하세요</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <Input
                    label="은행명"
                    type="text"
                    placeholder="예: 국민은행"
                    value={formData?.bankName}
                    onChange={(e) => handleChange('bankName', e?.target?.value)}
                    disabled={isLoading}
                  />
                  <Input
                    label="계좌번호"
                    type="text"
                    placeholder="000-0000-0000-00"
                    value={formData?.accountNumber}
                    onChange={(e) => handleChange('accountNumber', e?.target?.value)}
                    disabled={isLoading}
                  />
                  <Input
                    label="예금주"
                    type="text"
                    placeholder="예금주명"
                    value={formData?.accountHolder}
                    onChange={(e) => handleChange('accountHolder', e?.target?.value)}
                    disabled={isLoading}
                  />
                </div>

                <Input
                  label="결제 마감 기한 (일)"
                  type="number"
                  placeholder="3"
                  value={formData?.paymentDeadline}
                  onChange={(e) => handleChange('paymentDeadline', e?.target?.value)}
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* 운영 설정 카드 */}
            <div className="bg-muted/30 border border-border rounded-xl p-4 md:p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Settings" size={18} color="var(--color-accent)" />
                </div>
                <div>
                  <h3 className="text-base font-heading font-semibold text-foreground">운영 설정</h3>
                  <p className="text-xs text-muted-foreground">기본 운영 정보를 설정하세요</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Input
                    label="기본 코트 수"
                    type="number"
                    placeholder="4"
                    value={formData?.defaultCourts}
                    onChange={(e) => handleChange('defaultCourts', e?.target?.value)}
                    disabled={isLoading}
                  />
                  <Input
                    label="기본 세션 시간 (분)"
                    type="number"
                    placeholder="120"
                    value={formData?.defaultDuration}
                    onChange={(e) => handleChange('defaultDuration', e?.target?.value)}
                    disabled={isLoading}
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Input
                    label="최소 참가자 수"
                    type="number"
                    placeholder="8"
                    value={formData?.minParticipants}
                    onChange={(e) => handleChange('minParticipants', e?.target?.value)}
                    disabled={isLoading}
                  />
                  <Input
                    label="최대 참가자 수"
                    type="number"
                    placeholder="24"
                    value={formData?.maxParticipants}
                    onChange={(e) => handleChange('maxParticipants', e?.target?.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            {errors?.submit && (
              <div className="bg-error/10 border border-error/20 rounded-md p-4 flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <p className="text-sm text-error">{errors?.submit}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
                fullWidth
              >
                취소
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={isLoading}
                iconName="Save"
                iconPosition="right"
                fullWidth
              >
                등록
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VenueCreateModal;
